import { Col, Hr, Row } from "@amzn/stencil-react-components/layout";
import { Spinner } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { FormattedMessage } from "react-intl";

import { StepBodyStyledText } from "@/components/Commons/StepBodyStyledText";

import SecurityKeyEducationalContent from "./SecurityKeyEducationalContent";

/**
 * Screen component displayed while actively detecting security keys.
 *
 * This component provides educational content about security keys and shows a loading
 * spinner with a "detecting" message while the system scans for connected security keys.
 * The screen includes instructions for users to insert their security key and wait
 * for detection.
 *
 * @returns JSX element containing the detection screen with educational content and loading state
 */
export function SecurityKeyDetectionScreen(): JSX.Element {
  return (
    <Col rowGap="S400">
      <SecurityKeyEducationalContent />
      <StepBodyStyledText>
        <Col rowGap="S400">
          <Hr color="neutral20" size="narrow" />

          <Text
            fontSize="T300"
            fontWeight="bold"
            dataTestId="testSecurityKeyHeading"
          >
            <FormattedMessage id="insertAndTestSecurityKey-instructions-testSecurityKeyHeading" />
          </Text>

          <Text fontWeight="regular" dataTestId="testSecurityKeyInstruction">
            <FormattedMessage
              id="insertAndTestSecurityKey-instructions-testSecurityKeyInstruction"
              values={{
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </Text>
          <Row columnGap="S300" justifyContent="flex-start">
            <Spinner
              dataTestId="securityKeyDetectionSpinner"
              aria-label="Detecting security key, please wait"
            />
            <Text
              fontWeight="regular"
              color="color.text.secondary"
              dataTestId="detectingSecurityKeyMessage"
            >
              <FormattedMessage id="insertAndTestSecurityKey-detection-detectingMessage" />
            </Text>
          </Row>
        </Col>
      </StepBodyStyledText>
    </Col>
  );
}
